import React from "react";
import { Alert, Box, Grid, Stack } from "@mui/material";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from "@mui/lab";
import TimelineOppositeContent from "@mui/lab/TimelineOppositeContent";
import { useQuery } from "@apollo/client";
import { backendURL } from "../../constants";
import { GET_DIAMOND_ANALYSES, tokenOptions } from "../../backend/graphql";
import { useAppSelector } from "../../app/hooks";
import SectionTitle from "./SectionTitle";
import ColorSample from "./ColorSample";
import ZoomableImage from "./ZoomableImage";
import { timelineSx } from "./timelineSx";

type DiamondColorAnalysisProps = {
  diamondID: string;
};

// One step of the timeline: a label and what the step produced.
const Step = ({
  label,
  done,
  last,
  children,
}: {
  label: string;
  done: boolean;
  last: boolean;
  children?: React.ReactNode;
}) => (
  <TimelineItem>
    <TimelineOppositeContent />
    <TimelineSeparator>
      <TimelineDot color={done ? "primary" : undefined} />
      {!last && <TimelineConnector />}
    </TimelineSeparator>
    <TimelineContent>
      <Grid container direction="row">
        <Grid item xs={4}>
          {label}
        </Grid>
        <Grid item xs={8}>
          {children}
        </Grid>
      </Grid>
    </TimelineContent>
  </TimelineItem>
);

// What the stone's color is, read from everything its calibration stored — the original's and the
// pattern's histograms combined: the histogram, the patch drawn from it, and beside it the main
// color. It is read again by Analyze, in the row of buttons under the pictures.
const DiamondColorAnalysis = ({ diamondID }: DiamondColorAnalysisProps) => {
  const token = useAppSelector((state) => state.auth.token);
  const { data } = useQuery(GET_DIAMOND_ANALYSES, {
    ...tokenOptions(token),
    variables: { take: 1, skip: 0, filter: 'diamondID == "' + diamondID + '"' },
  });
  const analysis = data?.diamondAnalyses?.items[0];

  return (
    <>
      <SectionTitle>Analysis</SectionTitle>

      {/* Two different things: the run fell over, or the stone is one whose color is not read. The
          first is a fault to look at, the second is the pipeline doing as it was told. */}
      {analysis?.isError && (
        <Alert severity="error" sx={{ m: 1 }}>
          An error occurred when analyzing this stone.
        </Alert>
      )}
      {/* Read without both its sources — the original or the pattern missing — the color rests on
          one picture alone, and is flagged as lesser quality. */}
      {analysis?.isApproximate && (
        <Box sx={{ color: "error.main", fontWeight: 600, m: 1 }}>
          🚨 Approximate calibration as the original or the pattern image is
          missing
        </Box>
      )}
      {analysis?.isIgnored && (
        <Alert severity="info" sx={{ m: 1 }}>
          Unfortunately we are not able to produce a full analysis for this
          stone.
        </Alert>
      )}

      <Timeline sx={timelineSx}>
        {/* The original's and the pattern's colors as one distribution, drawn as three curves.
            Drawn large so it stays sharp; shown at the width of the column, and a click opens it
            at full size. */}
        <Step label="Histogram" done={!!analysis?.histogramID} last={false}>
          {analysis?.histogramID && (
            <ZoomableImage
              src={`${backendURL}/image/${analysis.histogramID}`}
              sx={{ width: "100%", maxWidth: 900, height: "auto" }}
            />
          )}
        </Step>
        {/* The patch, and to its right the main color read from it. */}
        <Step label="Patch" done={!!analysis?.colorPatchID} last={true}>
          <Stack direction="row" spacing={2} alignItems="center">
            {analysis?.colorPatchID && (
              <img
                alt=""
                width={124}
                src={`${backendURL}/image/${analysis.colorPatchID}`}
              />
            )}
            {analysis?.oKH != null && (
              <ColorSample
                l={analysis.oKL}
                c={analysis.oKC}
                h={analysis.oKH}
                size={124}
              />
            )}
          </Stack>
        </Step>
      </Timeline>
    </>
  );
};

export default DiamondColorAnalysis;
